import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { ConditionalAccess } from '../../../../../admin/components/ConditionalAccess';
import { copyToClipboard, getDefaultDateStringValue } from '../../../../../models/UiUtils';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { Contacts } from '../../../../containers/reporting/Campaigns/Contacts';
import { baseStyleSheet } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { ClipboardIcon } from '../../../svgs/icons/ClipboardIcon';
import { TemplateTypeTag } from '../../../templates/TemplateTypeTag';
import { GroupEmailTableRowAnalytics } from '../GroupEmailTableRowAnalytics';
import { styleSheet as groupStyleSheet } from '../GroupEmailTableRowExpandable/styles';
import { GroupEmailTableRowStatus } from '../GroupEmailTableRowStatus';
import { ContactFilterRequestTagList } from './ContactFilterRequestTagList';
import { styleSheet } from './styles';

export interface IGroupEmailTableRowCallbacks {
	onCancelClicked?(e: React.MouseEvent<HTMLElement>): void;
	onCheckChanged?: (checked: boolean) => void;
	onClick?(e: React.MouseEvent<HTMLElement>): void;
	onEditClicked?(e: React.MouseEvent<HTMLElement>): void;
	onRenderApprovalActions?(): React.ReactNode;
	onRescheduleClicked?(e: React.MouseEvent<HTMLElement>): void;
	onReviewAndApproveClicked?(e: React.MouseEvent<HTMLElement>): void;
	onSubjectClicked?(e: React.MouseEvent<HTMLElement>): void;
}

interface IProps extends IGroupEmailTableRowCallbacks {
	className?: string;
	isGroupItem?: boolean;
	styles?: StyleDeclarationValue[];
	campaign: Api.ICampaign;
}

function _GroupEmailTableRow({
	isGroupItem,
	onSubjectClicked,
	onReviewAndApproveClicked,
	onRenderApprovalActions,
	onCancelClicked,
	onRescheduleClicked,
	onEditClicked,
	onClick,
	onCheckChanged,
	styles,
	className,
	campaign,
}: IProps) {
	const userSession = useUserSession();
	const [checked, setChecked] = React.useState(false);
	const isHtmlNewsletter = campaign.categories && campaign.categories.indexOf(Api.EmailCategories.HtmlNewsletter) >= 0;
	const showAnalytics =
		campaign.status === Api.EmailSendStatus.Sending || campaign.status === Api.EmailSendStatus.Complete;
	const sampleContacts = (campaign.sampleContacts ?? []).map(c => new Api.ContactViewModel(userSession, c));
	const handleCheckChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
		ev.stopPropagation();
		setChecked(ev.target.checked);
		onCheckChanged(ev.target.checked);
	};
	const cancelDate = campaign?.cancelledDate ? new Date(campaign.cancelledDate) : null;
	const canEditEmailContent =
		(campaign.status === Api.EmailSendStatus.Ready || campaign.status === Api.EmailSendStatus.Queued) &&
		campaign.creator?.id === userSession?.user?.id &&
		!campaign.categories?.some(c => c === Api.EmailCategories.HtmlNewsletter);

	const handleCopyId = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		copyToClipboard(campaign?.id ?? '');
		toast.success('ID copied to clipboard');
	};
	const renderSubject = () => {
		const subject = campaign.subject;
		return (
			<div className={css(styleSheet.subjectCell, isGroupItem ? groupStyleSheet.groupItemSubject : null)}>
				<div className={css(styleSheet.subject)} onClick={onSubjectClicked}>
					{isHtmlNewsletter ? (
						<TemplateTypeTag
							emailCategory={Api.EmailCategories.HtmlNewsletter}
							styles={[styleSheet.subjectTemplateTypeTag]}
						/>
					) : null}
					{[Api.EmailSendStatus.Ready, Api.EmailSendStatus.Queued, Api.EmailSendStatus.WaitingForApproval].indexOf(
						campaign.status
					) === -1 ? (
						<NavLink
							to={`group-email/${campaign.id}`}
							className={css(baseStyleSheet.brandLink)}
							onClick={ev => {
								ev.preventDefault();
								onClick?.(ev);
							}}
						>
							{subject}
						</NavLink>
					) : (
						<div className={css(baseStyleSheet.brandLink)}>{subject}</div>
					)}
					<ConditionalAccess permission={AdminPermission.ReadAccount}>
						<span onClick={handleCopyId} className={css(styleSheet.copyIdIcon)}>
							<ClipboardIcon className={css(styleSheet.linkIcon)} />
						</span>
					</ConditionalAccess>
				</div>
				<GroupEmailTableRowStatus campaign={campaign} styles={[styleSheet.status]} />
			</div>
		);
	};
	const renderStatus = () => {
		let title: string = null;
		let dateStringValue: string = null;
		let count = campaign.totalEmails;
		switch (campaign.status) {
			case Api.EmailSendStatus.WaitingForApproval: {
				title = 'Scheduled';
				dateStringValue = getDefaultDateStringValue(campaign.schedule.startDate);
				break;
			}
			case Api.EmailSendStatus.Rejected: {
				title = 'Total';
				const rejectionDateStringValue = campaign.approvalRequests.find(
					x => x.response?.status === Api.CampaignApprovalStatus.Rejected
				)?.creationDate;
				dateStringValue = rejectionDateStringValue ? getDefaultDateStringValue(rejectionDateStringValue) : null;
				break;
			}
			case Api.EmailSendStatus.Cancelled: {
				title = 'Cancelled';
				// Essentially counts any failed or no emails as if they were cancelled
				count = campaign.totalEmails - campaign.sentSuccessfullyCount;
				break;
			}
			case Api.EmailSendStatus.Ready:
			case Api.EmailSendStatus.Queued: {
				title = 'Scheduled';
				dateStringValue = getDefaultDateStringValue(campaign.schedule.startDate);
				// Show email total reflecting the count that is already sent. We will not send again.
				count = campaign.totalEmails - campaign.sentSuccessfullyCount;
				break;
			}
			case Api.EmailSendStatus.Draft: {
				title = Api.EmailSendStatus.Draft;
				dateStringValue = getDefaultDateStringValue(campaign.schedule?.startDate);
				break;
			}
			default: {
				break;
			}
		}
		return (
			<div className={css(styleSheet.scheduleInfo)}>
				<div className={css(styleSheet.scheduleInfoCount)}>{count.toLocaleString()}</div>
				{!!title && (
					<div className={css(styleSheet.scheduleInfoDate)}>
						<div className={css(styleSheet.scheduleInfoDateTitle)}>{title}</div>
						{!!dateStringValue && <time dateTime={dateStringValue}>{dateStringValue}</time>}
					</div>
				)}
			</div>
		);
	};
	const renderApprovalStatus = () => {
		// only supports checking the first approval request
		const approvalRequest = campaign.approvalRequests[0];
		const actions = onRenderApprovalActions ? onRenderApprovalActions() : null;

		return (
			<div
				className={css(baseStyleSheet.flex, baseStyleSheet.flexCol, baseStyleSheet.flex1, baseStyleSheet.itemsCenter)}
			>
				<div className={css(styleSheet.approvalStatusTitle)}>Pending Approval</div>
				{actions ? (
					<div className={css(styleSheet.approvalStatusActions)}>{actions}</div>
				) : (
					approvalRequest?.approver?.accountId === userSession.account.id &&
					approvalRequest?.approver?.id === userSession.user.id &&
					!!onReviewAndApproveClicked && (
						<button
							className={css(baseStyleSheet.ctaButtonSmall, styleSheet.approvalStatusActions)}
							onClick={onReviewAndApproveClicked}
						>
							<span>Review & Approve</span>
						</button>
					)
				)}
				<ConditionalAccess permission={AdminPermission.CreateCampaigns}>
					{!cancelDate && (
						<button
							className={css(baseStyleSheet.brandLink, styleSheet.approvalStatusActions)}
							onClick={onCancelClicked}
						>
							<span>Cancel/Reject</span>
						</button>
					)}
				</ConditionalAccess>
			</div>
		);
	};
	const renderScheduleActions = () => {
		const readyOrQueued =
			campaign.status === Api.EmailSendStatus.Ready || campaign.status === Api.EmailSendStatus.Queued;
		// Unreliable to check based on schedule.startDate because schedule gets updated potentially
		const campaignHasStarted = campaign.sentSuccessfullyCount > 0;
		return (
			<div className={css(styleSheet.scheduleActionsWrapper)}>
				{canEditEmailContent && !!onEditClicked && (
					<button className={css(baseStyleSheet.brandLink, baseStyleSheet.p2)} onClick={onEditClicked}>
						<span>Edit</span>
					</button>
				)}
				<button className={css(baseStyleSheet.brandLink, baseStyleSheet.p2)} onClick={onRescheduleClicked}>
					<span>
						{campaign.status === Api.EmailSendStatus.Cancelled && campaignHasStarted ? 'Resume' : 'Reschedule'}
					</span>
				</button>
				{((!cancelDate && !campaign.status) || readyOrQueued) && (
					<button className={css(baseStyleSheet.brandLink, baseStyleSheet.p2)} onClick={onCancelClicked}>
						<span className={css(baseStyleSheet.wFull, baseStyleSheet.textCenter)}>Cancel</span>
					</button>
				)}
			</div>
		);
	};
	const renderCampaignAnalyticsCol = () => {
		return (
			<div className={css(styleSheet.analytics)}>
				{showAnalytics ? (
					<GroupEmailTableRowAnalytics campaign={campaign} onCancelClicked={onCancelClicked} />
				) : (
					<div className={css(styleSheet.analyticsContent)}>
						<div className={css(styleSheet.schedule)}>
							{renderStatus()}
							{campaign.sentSuccessfullyCount > 0 && (
								<div>
									<div className={css(styleSheet.count)}>{campaign.sentSuccessfullyCount.toLocaleString()}</div>
									<div className={css(styleSheet.action)}>emailed</div>
								</div>
							)}
						</div>
						{campaign.approvalRequests &&
						campaign.approvalRequests.length > 0 &&
						!campaign.approvalRequests.every(x => x.response?.status === Api.CampaignApprovalStatus.Approved) &&
						campaign.status !== Api.EmailSendStatus.Rejected &&
						campaign.status !== Api.EmailSendStatus.Cancelled
							? renderApprovalStatus()
							: campaign.status !== Api.EmailSendStatus.Draft && renderScheduleActions()}
					</div>
				)}
			</div>
		);
	};
	return (
		<div
			className={`${css(styleSheet.row, ...(styles || []))} ${className || ''}`}
			onClick={onClick}
			data-id={campaign?.id}
		>
			{onCheckChanged ? (
				<div className={css(styleSheet.checkboxCol)}>
					<Checkbox
						id={`campaign-checkbox-${campaign.id}`}
						checked={checked}
						onChange={handleCheckChange}
						onClick={e => {
							e.stopPropagation();
						}}
					/>
				</div>
			) : null}
			{renderSubject()}
			{campaign.filterRequest?.criteria ? (
				<ContactFilterRequestTagList maxTagsToDisplayInline={2} filterRequest={campaign.filterRequest} />
			) : null}
			{!campaign.filterRequest?.criteria && sampleContacts?.length ? (
				<div className={css(styleSheet.contacts)}>
					<Contacts maxContacts={3} totalContacts={campaign?.sentSuccessfullyCount} contacts={sampleContacts} />
				</div>
			) : null}
			{!campaign.filterRequest?.criteria && !sampleContacts?.length && campaign?.totalEmails ? (
				<div className={css(styleSheet.contacts)}>
					<span className={css(styleSheet.contactsCount)}>
						{campaign.totalEmails} contact{campaign.totalEmails > 1 ? 's' : ''}
					</span>
				</div>
			) : null}
			{renderCampaignAnalyticsCol()}
			<div className={css(styleSheet.placeholder)} />
		</div>
	);
}

export const GroupEmailTableRow = observer(_GroupEmailTableRow);
